/**
 * The web shell of the 3D dollhouse view: owns the canvas, the headless
 * View3DCore (camera, scene, rendering, picking) from the editor-core
 * package, and the frame loop. Its pointer and wheel input live in
 * View3DPointerInput, its camera keys in View3DKeyInput, and the DOM layers
 * it places each frame (label layer) in View3DOverlays. A host
 * can glide the camera to center a point (`centerOn`); the frame loop carries
 * the glide and the held keys on every frame. See View3DCore for the
 * wall-visibility model.
 */

import { FloorPlanRoom } from '@kaiizen/kai-floor-plan';
import {
  type CameraAngle,
  GL_CONTEXT_ATTRIBUTES,
  type Scene3D,
  type Scene3DOptions,
  Selection,
  type TurnDirection,
  type Vec3,
  View3DCore,
  type View3DQuality,
} from '@kaiizen/kai-floor-plan-editor-core';
import { View3DKeyInput } from './View3DKeyInput';
import { type LabelsListener, View3DOverlays } from './View3DOverlays';
import { View3DPointerInput, type View3DPointerOptions } from './View3DPointerInput';
import type { View3DTool } from './viewTools';

/** What a host can hear from the view. */
export interface View3DOptions<S> extends View3DPointerOptions<S> {
  onLabels?: LabelsListener;
  /** Which way the camera looks, after each frame that drew it looking a new way. */
  onAngle?: (angle: CameraAngle) => void;
}

/** The canvas's WebGL2 context, or WebGL where only that exists. */
const createGlContext = (canvas: HTMLCanvasElement): WebGLRenderingContext => {
  const webglContext = (canvas.getContext('webgl2', GL_CONTEXT_ATTRIBUTES)
    ?? canvas.getContext('webgl', GL_CONTEXT_ATTRIBUTES)) as WebGLRenderingContext | null;
  if (!webglContext) throw new Error('WebGL not available');
  return webglContext;
};

/**
 * `S` is what a click resolves to: the editor's `Selection` for a single
 * room shown via `setShape`, or the selection type of a composed scene shown
 * via `setScene` (the assembler selects whole rooms). A click on empty space
 * reports `null`.
 */
export class View3D<S = Selection> {
  private canvas: HTMLCanvasElement;
  private core: View3DCore<S>;
  private overlays: View3DOverlays;
  private pointerInput: View3DPointerInput<S>;
  private keyInput: View3DKeyInput;
  private running = false;
  private resizeObserver: ResizeObserver;
  private onAngle?: (angle: CameraAngle) => void;
  private drawnAngle: CameraAngle | null = null;

  constructor(canvas: HTMLCanvasElement, options: View3DOptions<S> = {}) {
    this.canvas = canvas;
    this.onAngle = options.onAngle;
    this.core = new View3DCore<S>(createGlContext(canvas));
    this.overlays = new View3DOverlays(canvas, options.onLabels);
    this.resizeObserver = new ResizeObserver(() => {
      this.core.dirty = true;
    });
    this.resizeObserver.observe(canvas);
    this.pointerInput = new View3DPointerInput(canvas, this.core, options);
    this.keyInput = new View3DKeyInput(canvas, this.core);
  }

  /** How the 3D view draws, from the next frame on. */
  setQuality(quality: View3DQuality): void {
    this.core.setQuality(quality);
  }

  /** Show one room (the editor); `gadget` false leaves the move gadget out (read-only). */
  setShape(this: View3D<Selection>, shape: FloorPlanRoom, selection?: Selection, gadget = true, options?: Scene3DOptions): void {
    this.core.setShape(shape, selection, gadget, options);
    this.overlays.setLabels(this.core.labels);
  }

  /** Show an already-built scene (the assembler's composition); see View3DCore.setScene. */
  setScene(scene: Scene3D<S>, reframe?: boolean): void {
    this.core.setScene(scene, reframe);
    this.overlays.setLabels(this.core.labels);
  }

  /** Glide back to the opening view: the whole model framed at the 3/4 angle. */
  goHome(): void {
    this.core.goHome();
  }

  /** Glide a quarter turn about the vertical, the floor staying flat. */
  turnView(direction: TurnDirection): void {
    this.core.turnView(direction);
  }

  /** Glide to look from `angle`, keeping the target and distance. */
  viewFrom(angle: CameraAngle): void {
    this.core.viewFrom(angle);
  }

  /** Look from `angle` at once (a view cube drag). */
  setCameraAngle(angle: CameraAngle): void {
    this.core.setCameraAngle(angle);
  }

  /** Look from `angle` when the first scene is framed; see View3DCore.openAt. */
  openAt(angle: CameraAngle): void {
    this.core.openAt(angle);
  }

  /** Which way the camera looks now. */
  cameraAngle(): CameraAngle {
    const { azimuth, elevation } = this.core.getCameraState();
    return { azimuth, elevation };
  }

  /** Glide the camera so `point` ends at the center of the view; see View3DCore.centerOn. */
  centerOn(point: Vec3): void {
    this.core.centerOn(point);
  }

  /** The drag tool; shift swaps to the other for one gesture. */
  setTool(tool: View3DTool): void {
    this.pointerInput.setTool(tool);
  }

  show(): void {
    this.overlays.show();
    if (this.running) return;
    this.running = true;
    this.keyInput.show();
    this.core.dirty = true;
    requestAnimationFrame(this.frame);
  }

  hide(): void {
    this.running = false;
    this.keyInput.hide();
    this.overlays.hide();
  }

  /**
   * Full teardown for host frameworks that mount/unmount the view: stops the
   * frame loop, disconnects observers and input, removes the DOM this view
   * added (labels), and releases the GL context.
   */
  dispose(): void {
    this.hide();
    this.resizeObserver.disconnect();
    this.pointerInput.dispose();
    this.keyInput.dispose();
    this.overlays.remove();
    this.core.dispose();
  }

  /** One turn of the frame loop: carry the glide and the held keys, and redraw when anything changed. */
  private frame = (time: number): void => {
    if (!this.running) return;
    this.core.advance(time);
    this.keyInput.moveByHeldKeys(time);
    if (this.core.dirty) {
      this.resize();
      this.draw();
    }
    requestAnimationFrame(this.frame);
  };

  private resize(): void {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = Math.max(1, Math.round(this.canvas.clientWidth * dpr));
    const h = Math.max(1, Math.round(this.canvas.clientHeight * dpr));
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
  }

  private draw(): void {
    const frame = this.core.draw(this.canvas.width, this.canvas.height, this.canvas.clientWidth, this.canvas.clientHeight);
    this.overlays.place(frame, this.core.labels);
    this.reportAngle(frame.angle);
  }

  private reportAngle(angle: CameraAngle): void {
    const { drawnAngle } = this;
    if (drawnAngle && drawnAngle.azimuth === angle.azimuth && drawnAngle.elevation === angle.elevation) return;
    this.drawnAngle = angle;
    this.onAngle?.(angle);
  }
}
